import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import RecebimentosTable from './RecebimentosTable';
import { buscarRecebimentosPorAction, buscarRecebimentosCapabilities } from 'features/financeiro/services/financeiroService';
import { getCompanyContext } from 'utils/setCompanyContext';

const parseMoney = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const formatBRL = (n) => `R$ ${(parseFloat(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const inferColumnType = (field) => {
  const explicit = typeof field === 'object' ? field?.type : null;
  if (explicit) return explicit;

  const key = typeof field === 'object' ? field?.key : String(field || '');
  const k = String(key || '').toLowerCase();
  if (
    k.startsWith('valor_') ||
    k.includes('valor') ||
    k.includes('desconto') ||
    k.includes('acrescimo') ||
    k.includes('receita') ||
    k.includes('despesa') ||
    k.includes('aporte')
  ) return 'money';
  return 'text';
};

const RecebimentosSection = ({ empresaDefault = 'MOV Fibra', dataRange = null }) => {
  const getEmpresaSelecionada = useCallback(
    () => getCompanyContext()?.companies?.[0]?.name || empresaDefault,
    [empresaDefault]
  );

  const [empresaAtual, setEmpresaAtual] = useState(getEmpresaSelecionada);
  const [aba, setAba] = useState('por_conta_receita');
  const [loading, setLoading] = useState(false);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [capabilities, setCapabilities] = useState([]);
  const [dadosAtuais, setDadosAtuais] = useState([]);
  const [erro, setErro] = useState(null);
  const requestId = useRef(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const atualizarEmpresa = () => {
      setEmpresaAtual(getEmpresaSelecionada());
    };

    atualizarEmpresa();

    window.addEventListener('company-changed', atualizarEmpresa);
    window.addEventListener('company-context-changed', atualizarEmpresa);

    return () => {
      window.removeEventListener('company-changed', atualizarEmpresa);
      window.removeEventListener('company-context-changed', atualizarEmpresa);
    };
  }, [getEmpresaSelecionada]);

  const opcoesVisaoFallback = useMemo(
    () => ([
      { value: 'por_conta_receita', label: 'Por Conta de Receita' },
      { value: 'por_conta_carteira', label: 'Por Conta/Carteira' },
      { value: 'por_usuarios', label: 'Por Usuários' }
    ]),
    []
  );

  const opcoesVisao = useMemo(() => {
    if (Array.isArray(capabilities) && capabilities.length > 0) {
      return capabilities
        .filter((a) => a && a.key)
        .map((a) => ({ value: a.key, label: a.label || a.key }));
    }
    return opcoesVisaoFallback;
  }, [capabilities, opcoesVisaoFallback]);

  // Usar o período do filtro se fornecido, senão usar padrão (mês atual)
  const periodoPadrao = useMemo(() => {
    if (dataRange && dataRange.start && dataRange.end) {
      return { data_inicio: dataRange.start, data_fim: dataRange.end };
    }
    const hoje = new Date();
    const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
    const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
    return { data_inicio: ini, data_fim: fim };
  }, [dataRange]);

  const carregar = useCallback(async (qual) => {
    const id = ++requestId.current;
    setLoading(true);
    setErro(null);
    setDadosAtuais([]);
    try {
      const params = { empresa: empresaAtual, ...periodoPadrao };
      const r = await buscarRecebimentosPorAction(qual, params);
      if (id !== requestId.current) return;
      if (!r.success) {
        setErro('Não foi possível carregar os recebimentos. Tente novamente.');
        return;
      }
      setDadosAtuais(Array.isArray(r.data) ? r.data : []);
    } catch {
      if (id === requestId.current) setErro('Não foi possível carregar os recebimentos. Tente novamente.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [empresaAtual, periodoPadrao]);

  useEffect(() => {
    carregar(aba);
    return () => { requestId.current += 1; };
  }, [carregar, aba]);

  useEffect(() => {
    let alive = true;
    const loadCapabilities = async () => {
      setCapabilitiesLoading(true);
      try {
        const r = await buscarRecebimentosCapabilities({ empresa: empresaAtual });
        if (!alive) return;
        setCapabilities(Array.isArray(r.actions) ? r.actions : []);
      } catch (error) {
        if (!alive) return;
        setCapabilities([]);
      } finally {
        if (!alive) return;
        setCapabilitiesLoading(false);
      }
    };

    if (empresaAtual) {
      loadCapabilities();
    }

    return () => {
      alive = false;
    };
  }, [empresaAtual]);

  useEffect(() => {
    if (!opcoesVisao || opcoesVisao.length === 0) return;
    if (opcoesVisao.some((o) => o.value === aba)) return;
    setAba(opcoesVisao[0].value);
  }, [opcoesVisao, aba]);

  // Colunas com encoding corrigido e melhor responsividade
  const colunasReceita = [
    { 
      key: 'conta', 
      label: 'Conta Receita', 
      sortable: true, 
      cellClassName: 'whitespace-normal break-words text-xs sm:text-sm min-w-[120px] max-w-[140px] lg:max-w-none' 
    },
    { 
      key: 'valor_desconto', 
      label: 'Descontos', 
      sortable: true, 
      render: (v) => formatBRL(v), 
      cellClassName: 'text-right whitespace-nowrap text-xs sm:text-sm' 
    },
    { 
      key: 'valor_acrescimo', 
      label: 'Acréscimos', 
      sortable: true, 
      render: (v) => formatBRL(v), 
      cellClassName: 'text-right whitespace-nowrap text-xs sm:text-sm' 
    },
    { 
      key: 'valor_baixo', 
      label: 'Valor Baixado', 
      sortable: true, 
      render: (v) => formatBRL(v), 
      cellClassName: 'text-right whitespace-nowrap text-xs sm:text-sm' 
    },
    { 
      key: 'valor_liquido_recebido', 
      label: 'Valor Líquido', 
      sortable: true, 
      render: (v) => formatBRL(v), 
      cellClassName: 'text-right whitespace-nowrap font-semibold text-xs sm:text-sm' 
    },
    { 
      key: 'descontos_adicionais', 
      label: 'Desc. Adicionais', 
      sortable: true, 
      render: (v) => formatBRL(v), 
      cellClassName: 'text-right whitespace-nowrap text-xs sm:text-sm' 
    },
  ];

  const colunasCarteira = [
    { 
      key: 'conta', 
      label: 'Conta/Carteira', 
      sortable: true, 
      cellClassName: 'whitespace-normal break-words text-xs sm:text-sm min-w-[120px] max-w-[140px] lg:max-w-none' 
    },
    ...colunasReceita.slice(1)
  ];

  const colunasUsuarios = [
    { 
      key: 'usuario', 
      label: 'Usuário', 
      sortable: true, 
      render: (v) => v || 'N/A', 
      cellClassName: 'whitespace-normal break-words text-xs sm:text-sm min-w-[120px] max-w-[140px] lg:max-w-none' 
    },
    ...colunasReceita.slice(1)
  ];

  const normalizarLista = (lista) =>
    (lista || []).map((i) => ({
      ...i,
      valor_desconto: parseMoney(i.valor_desconto),
      valor_acrescimo: parseMoney(i.valor_acrescimo),
      valor_baixo: parseMoney(i.valor_baixo),
      valor_liquido_recebido: parseMoney(i.valor_liquido_recebido),
      descontos_adicionais: parseMoney(i.descontos_adicionais),
    }));

  const selectedCapability = useMemo(
    () => (Array.isArray(capabilities) ? capabilities.find((c) => c?.key === aba) : null),
    [capabilities, aba]
  );

  const dynamicColumns = useMemo(() => {
    const fields = selectedCapability?.fields || selectedCapability?.columns;
    if (!Array.isArray(fields) || fields.length === 0) return null;

    return fields
      .map((f) => {
        if (typeof f === 'string') {
          const type = inferColumnType({ key: f });
          return {
            key: f,
            label: f,
            sortable: true,
            type,
            render: type === 'money' ? (v) => formatBRL(v) : undefined,
            cellClassName: type === 'money' ? 'text-right whitespace-nowrap text-xs sm:text-sm' : 'whitespace-normal break-words text-xs sm:text-sm'
          };
        }

        if (f && typeof f === 'object') {
          const key = f.key || f.id || f.field;
          if (!key) return null;
          const type = inferColumnType(f);
          const label = f.label || f.name || String(key);
          return {
            key,
            label,
            sortable: f.sortable ?? true,
            type,
            render: type === 'money' ? (v) => formatBRL(v) : f.render,
            cellClassName: f.cellClassName || (type === 'money'
              ? 'text-right whitespace-nowrap text-xs sm:text-sm'
              : 'whitespace-normal break-words text-xs sm:text-sm')
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [selectedCapability]);

  const normalizarListaPorColunas = useCallback((lista, columns) => {
    if (!Array.isArray(lista) || lista.length === 0) return [];
    if (!Array.isArray(columns) || columns.length === 0) return lista;

    const moneyKeys = columns
      .filter((c) => c?.type === 'money' || c?.type === 'number')
      .map((c) => c.key);

    if (moneyKeys.length === 0) return lista;

    return lista.map((item) => {
      const next = { ...item };
      moneyKeys.forEach((k) => {
        next[k] = parseMoney(next[k]);
      });
      return next;
    });
  }, []);

  // Renderização mobile melhorada
  const MobileList = ({ items, tipo, columns }) => (
    <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
      {items.length === 0 && !loading ? (
        <div className="text-center py-8 text-[color:var(--text-muted)] text-sm">
          Nenhum dado encontrado
        </div>
      ) : (
        items.map((i, idx) => (
          <div key={idx} className="border border-[color:var(--border)] rounded-lg p-3 bg-[color:var(--surface)] shadow-sm">
            <div className="text-sm font-semibold text-[color:var(--text-primary)] mb-2 line-clamp-2 break-words">
              {columns?.[0]?.key ? (i[columns[0].key] || '—') : (tipo === 'por_usuarios' ? (i.usuario || 'Sem Usuário') : i.conta)}
            </div>
            {Array.isArray(columns) && columns.length > 1 ? (
              <div className="grid grid-cols-2 gap-2 text-xs">
                {columns.slice(1, 7).map((c) => {
                  const raw = i?.[c.key];
                  const v = c.render ? c.render(raw, i, idx) : (raw ?? '—');
                  return (
                    <div key={c.key} className="flex justify-between gap-2">
                      <span className="text-[color:var(--text-muted)]">{c.label}:</span>
                      <span className="font-medium text-right truncate max-w-[10rem]">{v}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-[color:var(--text-muted)]">Descontos:</span>
                    <span className="font-medium text-right">{formatBRL(i.valor_desconto)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[color:var(--text-muted)]">Acréscimos:</span>
                    <span className="font-medium text-right">{formatBRL(i.valor_acrescimo)}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-[color:var(--text-muted)]">Baixado:</span>
                    <span className="font-medium text-right">{formatBRL(i.valor_baixo)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[color:var(--text-muted)]">Desc. Adicionais:</span>
                    <span className="font-medium text-right">{formatBRL(i.descontos_adicionais)}</span>
                  </div>
                </div>
                <div className="col-span-2 pt-2 border-t border-[color:var(--border)] mt-1">
                  <div className="flex justify-between items-center">
                    <span className="text-[color:var(--text-muted)] font-medium text-sm">Valor Líquido:</span>
                    <span className="text-base font-bold text-green-600">
                      {formatBRL(i.valor_liquido_recebido)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const onChangeVisao = (v) => {
    setAba(v);
    setIsDropdownOpen(false);
  };

  const renderConteudoAba = () => {
    const dadosRaw = dadosAtuais;

    const colunasFallback = ({
      por_conta_receita: colunasReceita,
      por_conta_carteira: colunasCarteira,
      por_usuarios: colunasUsuarios
    }[aba]) || colunasReceita;

    const colunas = dynamicColumns && dynamicColumns.length > 0 ? dynamicColumns : colunasFallback;
    const dados = dynamicColumns && dynamicColumns.length > 0
      ? normalizarListaPorColunas(dadosRaw, colunas)
      : normalizarLista(dadosRaw);

    if (erro) {
      return (
        <div role="alert" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 text-[color:var(--text-primary)]">
          <p>{erro}</p>
          <button type="button" onClick={() => carregar(aba)} className="self-start shrink-0 rounded-lg border border-[color:var(--border)] px-4 py-2 text-sm hover:bg-[color:var(--surface-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent)]">
            Tentar novamente
          </button>
        </div>
      );
    }

    if (loading && dados.length === 0) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)]"></div>
        </div>
      );
    }

    return (
      <>
        <div className="hidden sm:block">
          <RecebimentosTable
            data={dados}
            columns={colunas}
            loading={loading}
            maxHeight="max-h-[28rem]"
            emptyMessage="Nenhum dado encontrado"
          />
        </div>
        <div className="block sm:hidden">
          <MobileList 
            items={dados} 
            tipo={aba}
            columns={colunas}
          />
        </div>
      </>
    );
  };

  return (
    <div className="bg-[color:var(--surface)] rounded-lg border border-[color:var(--border)] overflow-hidden">
      {/* Header com layout otimizado para mobile */}
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-[color:var(--border)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)]">Recebimentos</h3>
          
          {/* Container do dropdown customizado */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-xs sm:text-sm text-[color:var(--text-muted)] hidden sm:block whitespace-nowrap flex-shrink-0">
              Visão:
            </label>
            <div className="relative w-full sm:w-56">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs sm:text-sm border border-[color:var(--border)] rounded-lg bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
              >
                <span className="truncate text-left">
                  {capabilitiesLoading ? 'Carregando…' : (opcoesVisao.find(opt => opt.value === aba)?.label || 'Selecionar')}
                </span>
                <svg className="h-3 w-3 sm:h-4 sm:w-4 text-[color:var(--text-muted)] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd" />
                </svg>
              </button>

              {isDropdownOpen && (
                <>
                  {/* Overlay para fechar o dropdown */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setIsDropdownOpen(false)}
                  />
                  
                  {/* Dropdown menu */}
                  <div className="absolute z-50 mt-1 w-full sm:w-56 bg-[color:var(--surface)] rounded-lg shadow-lg border border-[color:var(--border)] max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {opcoesVisao.map((opcao) => (
                        <button
                          key={opcao.value}
                          type="button"
                          onClick={() => onChangeVisao(opcao.value)}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm hover:bg-[color:var(--surface-muted)] transition-colors ${
                            aba === opcao.value ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' : 'text-[color:var(--text-primary)]'
                          }`}
                        >
                          {opcao.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-3 sm:p-4 lg:p-6">
        {renderConteudoAba()}
      </div>
    </div>
  );
};

export default RecebimentosSection;
